import { Controller } from "@hotwired/stimulus"

// Heim-vs-24h-Pflege Kostenvergleich. Alle Daten kommen server-seitig als JSON
// (PflegekostenRechnerController#load_vergleich_data), gerechnet wird nur hier.
export default class extends Controller {
  static values = { data: Object }

  static targets = [
    "pflegegradButton",
    "pg1Note",
    "bundeslandSelect",
    "jahrSelect",
    "qualitaetSelect",
    "steuerCheckbox",
    "umwidmungCheckbox",
    "entlastungCheckbox",

    "resultHeim",
    "heimGesamt",
    "heimZuschlag",

    "resultH24",
    "h24Preis",
    "h24PflegegeldLabel",
    "h24Pflegegeld",
    "h24Verhinderung",
    "h24EntlastungRow",
    "h24Entlastung",
    "h24UmwidmungRow",
    "h24Umwidmung",
    "h24SteuerRow",
    "h24Steuer",

    "deltaBox",
    "delta"
  ]

  connect() {
    this.formatter = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })
    this.preselectBundesland()
    this.selectPflegegradValue(3)
  }

  // Orts- und Anbieterseiten verlinken mit #bundesland=<slug>: Fragment statt Query,
  // damit Canonical, Edge-Cache und Linksignale auf der sauberen URL bleiben.
  preselectBundesland() {
    const slug = new URLSearchParams(window.location.hash.slice(1)).get("bundesland")
    if (!slug || !this.hasBundeslandSelectTarget) return
    const option = Array.from(this.bundeslandSelectTarget.options).find(o => o.value === slug)
    if (option) this.bundeslandSelectTarget.value = slug
  }

  selectPflegegrad(event) {
    this.selectPflegegradValue(parseInt(event.currentTarget.dataset.value))
  }

  selectPflegegradValue(pg) {
    this.selectedPflegegrad = pg

    this.pflegegradButtonTargets.forEach(btn => {
      const isSelected = parseInt(btn.dataset.value) === pg
      btn.classList.toggle("border-ph24_new_blue", isSelected)
      btn.classList.toggle("bg-ph24_new_blue/5", isSelected)
      btn.classList.toggle("ring-2", isSelected)
      btn.classList.toggle("ring-ph24_new_blue", isSelected)
      btn.classList.toggle("border-gray-200", !isSelected)
      btn.classList.toggle("hover:border-ph24_new_blue/40", !isSelected)
    })

    if (this.hasPg1NoteTarget) this.pg1NoteTarget.classList.toggle("hidden", pg !== 1)

    this.calculate()
  }

  calculate() {
    if (!this.selectedPflegegrad) return
    const data = this.dataValue
    const pg = this.selectedPflegegrad

    // Pflegeheim: Bundesland-Gesamtkosten minus Leistungszuschlag (§ 43c, ab PG 2).
    // Der Zuschlag mindert nur den pflegebedingten Anteil (EEE des Bundeslands,
    // beim Bundesdurchschnitt der gewichtete Bund-EEE) — reproduziert die
    // offiziellen vdek-Werte je Aufenthaltsjahr.
    const slug = this.bundeslandSelectTarget.value
    const durchschnitt = slug === "durchschnitt"
    const heimGesamt = durchschnitt ? data.bund_eigenanteil : data.bundesland_eigenanteil[slug]
    const eee = durchschnitt ? data.eee_durchschnitt : data.bundesland_eee[slug]

    const jahr = this.jahrSelectTarget.value
    const zuschlag = pg >= 2 ? Math.round(data.leistungszuschlag[jahr] * eee) : 0
    const heim = heimGesamt - zuschlag

    // 24h-Pflege: Agenturpreis minus anrechenbare Leistungen. Pflegegeld fließt
    // voll, da die Betreuungskraft keine Pflegesachleistung erbringt — außer beim
    // Umwandlungsanspruch (§ 45a Abs. 4): 40 % der Sachleistung umwidmen zählt als
    // Kombinationsleistung und kürzt das Pflegegeld anteilig auf 60 %.
    const leistungen = data.leistungen[pg] || {}
    const preis = data.preise_24h[this.qualitaetSelectTarget.value].preis
    const pflegegeldVoll = pg >= 2 ? (leistungen["Pflegegeld"] || 0) : 0
    const verhinderung = pg >= 2 ? Math.round(data.verhinderungspflege_jahr / 12) : 0

    // § 45b ist zweckgebunden: zählt nur bei nach Landesrecht (§ 45a) anerkannter
    // Agentur — deshalb Opt-in statt Standardabzug.
    const entlastungAktiv = this.entlastungCheckboxTarget.checked
    const entlastung = entlastungAktiv ? (leistungen["Entlastungsbetrag"] || 0) : 0

    const umwidmungAktiv = pg >= 2 && this.umwidmungCheckboxTarget.checked
    const pflegegeld = umwidmungAktiv ? Math.round(pflegegeldVoll * 0.6) : pflegegeldVoll
    const umwidmung = umwidmungAktiv ? Math.round((leistungen["Pflegesachleistungen"] || 0) * 0.4) : 0

    const steuerAktiv = this.steuerCheckboxTarget.checked
    const steuer = steuerAktiv ? Math.round(data.steuerbonus_35a_jahr / 12) : 0
    const h24 = Math.max(0, preis - pflegegeld - verhinderung - entlastung - umwidmung - steuer)

    this.resultHeimTarget.textContent = this.formatter.format(heim)
    this.heimGesamtTarget.textContent = this.formatter.format(heimGesamt)
    this.heimZuschlagTarget.textContent = this.formatter.format(zuschlag)

    this.resultH24Target.textContent = this.formatter.format(h24)
    this.h24PreisTarget.textContent = this.formatter.format(preis)
    this.h24PflegegeldLabelTarget.textContent = umwidmungAktiv ? "− Pflegegeld (60 % wegen Umwandlung)" : "− Pflegegeld"
    this.h24PflegegeldTarget.textContent = this.formatter.format(pflegegeld)
    this.h24VerhinderungTarget.textContent = this.formatter.format(verhinderung)
    this.h24EntlastungRowTarget.classList.toggle("hidden", !entlastungAktiv)
    this.h24EntlastungTarget.textContent = this.formatter.format(entlastung)
    this.h24UmwidmungRowTarget.classList.toggle("hidden", !umwidmungAktiv)
    this.h24UmwidmungTarget.textContent = this.formatter.format(umwidmung)
    this.h24SteuerRowTarget.classList.toggle("hidden", !steuerAktiv)
    this.h24SteuerTarget.textContent = this.formatter.format(steuer)

    const diff = heim - h24
    this.deltaBoxTarget.classList.toggle("hidden", Math.abs(diff) < 1)
    this.deltaTarget.textContent = diff > 0
      ? `Die 24h-Pflege zu Hause ist in diesem Szenario ${this.formatter.format(diff)} pro Monat günstiger als das Pflegeheim.`
      : `Das Pflegeheim ist in diesem Szenario ${this.formatter.format(Math.abs(diff))} pro Monat günstiger als die 24h-Pflege zu Hause.`
  }
}
